import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useDatasetStore,
  effectiveDashboardName,
} from '@/store/dataset-store';

/**
 * Inline-editable dashboard label rendered next to the app title.
 * Local state mirrors the entry so typing is responsive; commits to
 * the store on blur / Enter. Empty submission falls back to the file
 * basename so users can always see *something*.
 *
 * Pulled out of App.tsx during R-6.
 */
export function DashboardNameInput({
  entry,
}: {
  entry: { id: string; fileName: string; dashboardName?: string };
}): JSX.Element {
  const { t } = useTranslation();
  const setDashboardName = useDatasetStore((s) => s.setDashboardName);
  const effective = effectiveDashboardName(entry);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(effective);

  // Re-seed local draft when switching datasets or after an external
  // setDashboardName call (e.g. persisted name loaded post-upload).
  // Exiting edit mode on dataset swap avoids carrying half-typed
  // names across datasets.
  useEffect(() => {
    setDraft(effective);
    setEditing(false);
  }, [effective]);

  const startEdit = (): void => {
    setDraft(effective);
    setEditing(true);
  };

  const commit = (): void => {
    const next = draft.trim();
    if (next && next !== effective) {
      setDashboardName(entry.id, next);
    } else {
      setDraft(effective);
    }
    setEditing(false);
  };

  const cancel = (): void => {
    setDraft(effective);
    setEditing(false);
  };

  if (!editing) {
    return (
      <div className="flex items-center gap-1.5 flex-1 min-w-0 max-w-md">
        <span
          className="text-sm truncate"
          style={{ color: 'var(--color-fg)' }}
          title={effective}
        >
          {effective}
        </span>
        <button
          type="button"
          onClick={startEdit}
          aria-label={t('dashboardName.edit')}
          title={t('dashboardName.edit')}
          className="shrink-0 flex items-center justify-center p-1 rounded hover:opacity-80 border-0 bg-transparent cursor-pointer"
          style={{ color: 'var(--color-muted)' }}
        >
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden>
            <path
              d="M11 2.5l2.5 2.5L5 13.5H2.5V11L11 2.5z"
              stroke="currentColor"
              strokeWidth="1.4"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5 flex-1 min-w-0 max-w-md">
      <input
        type="text"
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          else if (e.key === 'Escape') cancel();
        }}
        placeholder={effective}
        aria-label={t('dashboardName.label')}
        className="text-sm px-2 py-0.5 rounded flex-1 min-w-0 outline-none"
        style={{
          background: 'var(--color-bg)',
          color: 'var(--color-fg)',
          border: '1px solid var(--color-border)',
        }}
      />
      <button
        type="button"
        onClick={commit}
        className="shrink-0 text-xs px-2 py-1 rounded text-white border-0 cursor-pointer"
        style={{ background: 'var(--color-accent)' }}
      >
        {t('common.save')}
      </button>
    </div>
  );
}
